/**
 * Error Reporter — sends client-side errors to the backend for admin visibility.
 * Non-blocking: errors are reported silently, never interrupting the user flow.
 */

import { getTenantSlug, whenTenantResolved } from '../context/TenantContext'

const API_URL = import.meta.env.VITE_API_URL || '/api'

interface ErrorReport {
  patientNumber?: string
  phoneNumber?: string
  step?: string
  errorType: string
  errorMessage: string
  errorStack?: string
  endpoint?: string
  statusCode?: number
  metadata?: Record<string, unknown>
}

/**
 * Report an error to the backend. Never throws — fails silently.
 */
export async function reportError(report: ErrorReport): Promise<void> {
  try {
    await whenTenantResolved()
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    const slug = getTenantSlug()
    if (slug) headers['X-Tenant-ID'] = slug

    await fetch(`${API_URL}/error-logs`, {
      method: 'POST',
      headers,
      body: JSON.stringify(report),
    })
  } catch {
    // Silently fail — don't create error loops
  }
}

/**
 * Report an API error from an Axios error response.
 */
export function reportApiError(
  error: unknown,
  context: { patientNumber?: string; phoneNumber?: string; step?: string; endpoint?: string }
): void {
  const axiosError = error as {
    response?: { status?: number; data?: { error?: string } }
    message?: string
    config?: { url?: string; method?: string }
  }

  reportError({
    ...context,
    endpoint: context.endpoint || axiosError.config?.url,
    errorType: 'api_error',
    errorMessage: axiosError.response?.data?.error || axiosError.message || 'Unknown API error',
    statusCode: axiosError.response?.status,
    metadata: {
      method: axiosError.config?.method?.toUpperCase(),
    },
  })
}

/**
 * Report a Yousign-specific error.
 */
export function reportYousignError(
  error: unknown,
  context: { patientNumber?: string; step?: string; signatureRequestId?: string }
): void {
  const axiosError = error as {
    response?: { status?: number; data?: { type?: string; detail?: string } }
    message?: string
  }

  reportError({
    ...context,
    errorType: 'yousign_error',
    errorMessage: axiosError.response?.data?.detail || axiosError.message || 'Yousign error',
    statusCode: axiosError.response?.status,
    metadata: {
      yousignErrorType: axiosError.response?.data?.type,
      signatureRequestId: context.signatureRequestId,
    },
  })
}

/**
 * Report a file upload error.
 */
export function reportUploadError(
  error: unknown,
  context: { patientNumber?: string; step?: string; filename?: string }
): void {
  const err = error as { message?: string; response?: { status?: number } }

  reportError({
    ...context,
    errorType: 'upload_error',
    errorMessage: err.message || 'Upload failed',
    statusCode: err.response?.status,
    metadata: { filename: context.filename },
  })
}
